import { IAzureResource } from 'app/interfaces/IAzureResource';
import { LoggerService } from 'app/logger/logger.service';
import { ResourceType } from '../globals';
import { CustomPerformanceEvent } from 'app/logger/logger.custom-performance-event';
import { IAutosaveResourceProvider } from './interfaces/IAutosaveResourceProvider';
import { IAutosaveClient } from './interfaces/IAutosaveClient';
import _ from 'lodash';
import { IAutosaveAzureResource } from './interfaces/IAutosaveAzureResource';
import { convertObjectToString } from 'app/shared/utils/string.util';
import * as Constants from 'app/services/globals';
import { ResourceFilesUtils } from 'app/shared/utils/resourcefiles.utils';
import { IFullResource } from 'app/interfaces/IResourceFilesProvider';
import { ResourceId } from 'app/shared/utils/resource-id';
import { LoadingStatusEnum } from 'shell-models-lib';

export class AutosaveResourceProvider implements IAutosaveResourceProvider {
    public static className = 'AutosaveResourceProvider';
    protected resourceProviderName = __IS_A365__ ? Constants.WORKSPACE_RESOURCE_PROVIDER : Constants.FACTORY_RESOURCE_PROVIDER;
    protected topResourceType = __IS_A365__ ? Constants.WORKSPACE_RESOURCE_TYPE : Constants.FACTORY_RESOURCE_TYPE;
    private _resources: Map<ResourceType, IAzureResource[]> = new Map<ResourceType, IAzureResource[]>();
    // data format: notebook: nameInPublish, currentName
    private _renameMap: Map<ResourceType, Map<string, string>> = new Map<ResourceType, Map<string, string>>();

    public get renameMap(): Map<ResourceType, Map<string, string>> {
        return this._renameMap;
    }

    constructor(
        protected readonly _logger: LoggerService,
        private _autosaveClient: IAutosaveClient
    ) { }

    public reset(): void {
        this._resources.clear();
        this._renameMap.clear();
    }

    public getResourcesByType(resourceType: ResourceType): IAzureResource[] {
        if (this._resources.get(resourceType)) {
            return this._resources.get(resourceType);
        }
        return [];
    }

    /**
     * Return a list of resources stored in user store if allowed (autosave enabled, resource type enabled and the current session is active)
     * Otherwise, return empty array
     * @param resourceType the type of resourcec which is used in the request
     * @param handleRename for discardAll, handleRename is not needed
     */
    public async fetchResourcesByType(resourceType: ResourceType, handleRename = true): Promise<IAzureResource[]> {
        this._logger.log(AutosaveResourceProvider.className, `populateResourceByTypeFromUserStore - start downloading resources from user store for type: ${resourceType}`);
        const resultSet: IAzureResource[] = [];

        // used in log
        const startTime = performance.now();
        let resourceCount: number = 0;
        let isSuccessful = false;

        try {
            // first page
            let resp = await this._autosaveClient.getFirstPage(resourceType);
            resultSet.push(...this._getAzureResources(resp, resourceType));

            // next page
            while (resp.nextLink) {
                resp = await this._autosaveClient.getNextPage(resp.nextLink);
                resultSet.push(...this._getAzureResources(resp, resourceType));
            }

            // used in log
            resourceCount = resultSet.length;
            isSuccessful = true;
        } catch (error) {
            this._logger.error(
                AutosaveResourceProvider.className,
                `populateResourceByTypeFromUserStore - failed, type: ${resourceType}, error: ${convertObjectToString(error)}`,
            );
        }

        const endTime = performance.now();
        this._logger.logEvent(new CustomPerformanceEvent(AutosaveResourceProvider.className, 'populateResourceByTypeFromUserStore', {
            startTime, endTime, duration: endTime - startTime, isSuccessful,
            resourceType, resourceCount
        }));
        this._logger.log(AutosaveResourceProvider.className, `populateResourceByTypeFromUserStore - finished downloading resources from user store for type: ${resourceType}, count: ${resultSet.length}`);

        // store the info
        this._resources.set(resourceType, resultSet);

        const handledResult = this._postFetch(resourceType, resultSet, handleRename);
        return handledResult;
    }

    public convertAzureResourcesToFullResources(resources: IAzureResource[]): IFullResource[] {
        const newResources = new Array<IFullResource>();
        for (const azureResource of resources) {
            if (!azureResource) {
                this._logger.error(AutosaveResourceProvider.className, 'convertAzureResourcesToFullResources - azureResource is not defined, skipping');
                continue;
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const azureResourceProps = azureResource.properties as any;
            const resourceId = ResourceId.parseFrom(azureResource.id, this.resourceProviderName, this.topResourceType);
            if (!resourceId) {
                this._logger.error(AutosaveResourceProvider.className,
                    `convertAzureResourcesToFullResources - resourceId is not defined, skipping. Raw resource id: "${azureResource.id}"`);
                continue;
            }

            const resourceType = ResourceFilesUtils.resourceTypeFromAzureResource(azureResource);
            const resourceName = ResourceFilesUtils.correctResourceName(resourceId);
            const newResource: IFullResource = {
                name: resourceName,
                type: resourceType,
                folderName: azureResourceProps && azureResourceProps.folder ? azureResourceProps.folder.name : undefined,
                tags: azureResourceProps ? azureResourceProps.annotations : undefined,
                etag: azureResource.etag,
                status: LoadingStatusEnum.Ok,
                payload: JSON.stringify(azureResource),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                isV2: azureResource.properties && (azureResource.properties as any).schema != null
            };
            newResources.push(newResource);
        }

        return newResources;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _getAzureResources(resp: any, resourceType: ResourceType): IAzureResource[] {
        const result = resp.value ? resp.value : resp;
        if (result) {
            // the returned value is not in right format. Do data adapter
            const candidateItems: IAutosaveAzureResource[] = result as IAutosaveAzureResource[];
            const finalResults: IAzureResource[] = [];
            candidateItems.forEach((item: IAutosaveAzureResource) => {
                // ignore the one with incorrect id format
                const id = this._convertId(item.id, resourceType);
                if (id) {
                    const updatedItem: IAzureResource = {
                        id: this._convertId(item.id, resourceType),
                        name: item.name,
                        etag: item.etag,
                        properties: item.properties.typeProperties
                    };
                    finalResults.push(updatedItem);
                }
            });
            return finalResults;
        }
        return [];
    }

    /**
     * Update the renameMap and the updated the fetch value based on the rename info. The changes are mainly about name and id
     */
    private _postFetch(resourceType: ResourceType, originalResources: IAzureResource[], handleRename = true): IAzureResource[] {
        const resources = _.cloneDeep(originalResources);
        if (handleRename) {
            // clear original rename info
            this._renameMap.set(resourceType, new Map<string, string>());

            resources.forEach((resource: IAzureResource) => {
                if (resource.properties && resource.properties._changeType === 'rename' && resource.properties._originalName) {
                    const originalName = resource.properties._originalName;
                    const currentName = resource.name;
                    this._renameMap.get(resourceType).set(originalName, resource.name);

                    resource.name = originalName;
                    resource.id = resource.id.replace(`/${resourceType}s/${currentName}`, `/${resourceType}s/${originalName}`);
                }
            });
        }
        return resources;
    }

    /**
     * Convert id in user store into artifact service one
     * @param idInUserStore: /subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Synapse/workspaces/{workspaceName}/userStores/{artifactName}/draftNotebooks
     * Returned id format: /subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Synapse/workspaces/{workspaceName}/{artifactType}/{artifactName}
     */
    private _convertId(idInUserStore: string, resourceType: ResourceType): string | undefined {
        const splits = idInUserStore.split('/');
        if (splits.length >= 5
            && splits[1].toLowerCase() === 'subscriptions'
            && splits[3].toLowerCase() === 'resourcegroups'
            && splits[5].toLowerCase() === 'providers'
        ) {
            return `/subscriptions/${splits[2]}/resourceGroups/${splits[4]}/providers/Microsoft.Synapse/workspaces/${splits[8]}/${resourceType}s/${splits[10]}`;
        } else {
            this._logger.error(AutosaveResourceProvider.className, `_convertId - the input id is not in right format. id: ${idInUserStore}`);
            return undefined;
        }
    }
}
